import React, { useState, useRef, useEffect } from 'react';

const countries = [
	{ code: 'NG', dialCode: '234', flag: '🇳🇬', name: 'Nigeria' },
	{ code: 'GH', dialCode: '233', flag: '🇬🇭', name: 'Ghana' },
	{ code: 'KE', dialCode: '254', flag: '🇰🇪', name: 'Kenya' },
	{ code: 'ZA', dialCode: '27', flag: '🇿🇦', name: 'South Africa' },
	{ code: 'ET', dialCode: '251', flag: '🇪🇹', name: 'Ethiopia' },
	{ code: 'TZ', dialCode: '255', flag: '🇹🇿', name: 'Tanzania' },
	{ code: 'UG', dialCode: '256', flag: '🇺🇬', name: 'Uganda' },
	{ code: 'CI', dialCode: '225', flag: '🇨🇮', name: "Côte d'Ivoire" },
	{ code: 'CM', dialCode: '237', flag: '🇨🇲', name: 'Cameroon' },
	{ code: 'SN', dialCode: '221', flag: '🇸🇳', name: 'Senegal' },
	{ code: 'EG', dialCode: '20', flag: '🇪🇬', name: 'Egypt' },
	{ code: 'MA', dialCode: '212', flag: '🇲🇦', name: 'Morocco' },
	{ code: 'GB', dialCode: '44', flag: '🇬🇧', name: 'United Kingdom' },
	{ code: 'US', dialCode: '1', flag: '🇺🇸', name: 'United States' },
	{ code: 'CA', dialCode: '1', flag: '🇨🇦', name: 'Canada' },
	{ code: 'IN', dialCode: '91', flag: '🇮🇳', name: 'India' },
	{ code: 'CN', dialCode: '86', flag: '🇨🇳', name: 'China' },
	{ code: 'FR', dialCode: '33', flag: '🇫🇷', name: 'France' },
	{ code: 'DE', dialCode: '49', flag: '🇩🇪', name: 'Germany' },
];

const DEFAULT_COUNTRY = countries[0]; // Nigeria

function stripLeadingZero(number) {
	return number.startsWith('0') ? number.slice(1) : number;
}

function formatE164(dialCode, number) {
	const stripped = stripLeadingZero(number.replace(/\D/g, ''));
	return stripped ? `+${dialCode}${stripped}` : '';
}

function parseE164(value, countries) {
	if (!value || !value.startsWith('+')) {
		return { country: DEFAULT_COUNTRY, local: value || '' };
	}
	const raw = value.slice(1);
	const matched = countries
		.slice()
		.sort((a, b) => b.dialCode.length - a.dialCode.length)
		.find(c => raw.startsWith(c.dialCode));
	if (matched) {
		return { country: matched, local: raw.slice(matched.dialCode.length) };
	}
	return { country: DEFAULT_COUNTRY, local: raw };
}

const PhoneInput = ({ name, value, onChange, placeholder = 'Phone Number', readOnly = false, className = 'form-control' }) => {
	const parsed = parseE164(value, countries);
	const [country, setCountry] = useState(parsed.country);
	const [localNumber, setLocalNumber] = useState(parsed.local);
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const dropdownRef = useRef(null);

	useEffect(() => {
		const parsed = parseE164(value, countries);
		setCountry(parsed.country);
		setLocalNumber(parsed.local);
	}, [value]);

	useEffect(() => {
		const handleClickOutside = e => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setOpen(false);
				setSearch('');
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const emitChange = (dialCode, number) => {
		const formatted = formatE164(dialCode, number);
		onChange({ target: { name, value: formatted } });
	};

	const handleNumberChange = e => {
		const number = e.target.value.replace(/[^\d]/g, '');
		setLocalNumber(number);
		emitChange(country.dialCode, number);
	};

	const handleCountrySelect = c => {
		setCountry(c);
		setOpen(false);
		setSearch('');
		emitChange(c.dialCode, localNumber);
	};

	const filtered = countries.filter(
		c =>
			c.name.toLowerCase().includes(search.toLowerCase()) ||
			c.dialCode.includes(search) ||
			c.code.toLowerCase().includes(search.toLowerCase())
	);

	return (
		<div style={{ display: 'flex', gap: 0 }} ref={dropdownRef}>
			<div style={{ position: 'relative' }}>
				<button
					type="button"
					onClick={() => !readOnly && setOpen(o => !o)}
					disabled={readOnly}
					style={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						alignSelf: 'stretch',
						gap: 4,
						padding: '10px 10px',
						border: '1px solid #ced4da',
						borderRight: 'none',
						borderRadius: '0 4px 4px 0',
						background: readOnly ? '#e9ecef' : '#fff',
						cursor: readOnly ? 'default' : 'pointer',
						whiteSpace: 'nowrap',
						fontSize: 14,
					}}
				>
					<span style={{ fontSize: 18 }}>{country.flag}</span>
					<span style={{ color: '#495057' }}>+{country.dialCode}</span>
					{!readOnly && <span style={{ fontSize: 10, color: '#6c757d' }}>▼</span>}
				</button>

				{open && (
					<div
						style={{
							position: 'absolute',
							top: '100%',
							left: 0,
							zIndex: 1000,
							background: '#fff',
							border: '1px solid #ced4da',
							borderRadius: 4,
							boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
							width: 240,
							maxHeight: 280,
							overflowY: 'auto',
						}}
					>
						<div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
							<input
								autoFocus
								type="text"
								placeholder="Search country..."
								value={search}
								onChange={e => setSearch(e.target.value)}
								style={{
									width: '100%',
									padding: '4px 8px',
									border: '1px solid #ced4da',
									borderRadius: 4,
									fontSize: 13,
								}}
							/>
						</div>
						{filtered.map(c => (
							<div
								key={c.code}
								onClick={() => handleCountrySelect(c)}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									padding: '7px 12px',
									cursor: 'pointer',
									fontSize: 13,
									background: c.code === country.code ? '#f0f7ff' : 'transparent',
								}}
								onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
								onMouseLeave={e => (e.currentTarget.style.background = c.code === country.code ? '#f0f7ff' : 'transparent')}
							>
								<span style={{ fontSize: 18 }}>{c.flag}</span>
								<span style={{ flex: 1 }}>{c.name}</span>
								<span style={{ color: '#6c757d' }}>+{c.dialCode}</span>
							</div>
						))}
					</div>
				)}
			</div>

			<input
				name={name}
				type="tel"
				className={className}
				placeholder={placeholder}
				value={localNumber}
				onChange={handleNumberChange}
				readOnly={readOnly}
				style={{ borderRadius: '0 4px 4px 0', flex: 1 }}
			/>
		</div>
	);
};

export default PhoneInput;
